import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  UserIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XCircleIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  ArrowPathIcon,
  LockClosedIcon,
  LockOpenIcon,
  BookOpenIcon,
  UsersIcon,
  AcademicCapIcon,
  CreditCardIcon,
  DocumentArrowUpIcon,
  UserPlusIcon,
  TableCellsIcon,
  FingerPrintIcon,
  DevicePhoneMobileIcon,
  IdentificationIcon,
  KeyIcon,
  BoltIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { Menu, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import PhoneNumberInput from '@/components/ui/PhoneNumberInput'
import { useAuth } from '@/hooks/useAuth'
import { useUserRole } from '@/hooks/useUserRole'
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  getUserStats,
  deleteBiometric,
  assignRFIDCard,
  resetCardPIN,
  getCardByNumber,
  getUnassignedCards,
  lookupUserByContact,
  type User as ApiUser,
} from '@/api/usersApi'
import { getRoles } from '@/api/rolesApi'
import { getGrades, getStreams, type Grade, type Stream } from '@/api/academicApi'
import toast from 'react-hot-toast'

// Form schema for adding new student
const addStudentSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Valid email address is required'),
  phone: z.string().optional(),
  countryCode: z.string().optional(),
  studentId: z.string().optional(),
  gradeId: z.string().min(1, 'Grade is required'),
  streamId: z.string().min(1, 'Stream is required'),
  dateOfBirth: z.string().min(1, 'Date of birth is required'),
  parentEmail: z.string().optional().refine(val => !val || val === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), {
    message: 'Please enter a valid email address'
  }),
  parentPhone: z.string().optional(),
  roleId: z.string().optional(), // Auto-set behind the scenes
})

type AddStudentData = z.infer<typeof addStudentSchema>

// Bulk onboard schema
const bulkStudentSchema = z.object({
  students: z.array(z.object({
    firstName: z.string().min(2, 'First name must be at least 2 characters'),
    lastName: z.string().min(2, 'Last name must be at least 2 characters'),
    email: z.string().email('Valid email address is required'),
    phone: z.string().optional(),
    studentId: z.string().optional(),
    grade: z.string().min(1, 'Grade/Class is required'),
    stream:z.string().min(1, 'Strean is required'),
    dateOfBirth: z.string().min(1, 'Date of birth is required'),
    parentEmail: z.string().email('Valid parent email is required').optional(),
    parentPhone: z.string().optional(),
  })).min(1, 'At least one student is required'),
  roleId: z.string().min(1, 'Role is required'),
})

type BulkStudentData = z.infer<typeof bulkStudentSchema>

// Edit student schema
const editStudentSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Valid email address is required'),
  phone: z.string().optional(),
  countryCode: z.string().optional(),
  studentId: z.string().optional(),
  gradeId: z.string().min(1, 'Grade is required'),
  streamId: z.string().min(1, 'Stream is required'),
  dateOfBirth: z.string().optional(),
  parentEmail: z.string().optional().refine(val => !val || val === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), {
    message: 'Please enter a valid email address'
  }),
  parentPhone: z.string().optional(),
})

type EditStudentData = z.infer<typeof editStudentSchema>

const StudentsPage: React.FC = () => {
  const { userRole } = useUserRole()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  // Stream filter from URL params (from GradesPage navigation)
  const streamIdFromUrl = searchParams.get('streamId')
  const streamNameFromUrl = searchParams.get('streamName')
  const gradeNameFromUrl = searchParams.get('gradeName')

  const [students, setStudents] = useState<ApiUser[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [gradeFilter, setGradeFilter] = useState<string>('all')
  const [parentFilter, setParentFilter] = useState<string>('all')
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false)
  const [studentIdSearch, setStudentIdSearch] = useState('')
  const [parentNameSearch, setParentNameSearch] = useState('')
  const [teacherSearch, setTeacherSearch] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingStudent, setEditingStudent] = useState<ApiUser | null>(null)
  const [selectedStudent, setSelectedStudent] = useState<ApiUser | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table')

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalStudents, setTotalStudents] = useState(0)
  const [pageSize] = useState(15) // Show more students per page

  // Sorting state
  const [sortBy, setSortBy] = useState<'createdAt' | 'firstName' | 'lastName' | 'email' | 'lastLogin'>('createdAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // Available student roles from API
  const [studentRoles, setStudentRoles] = useState<{ id: string, name: string }[]>([])

  // Academic data state
  const [grades, setGrades] = useState<Grade[]>([])
  const [streams, setStreams] = useState<Stream[]>([])
  const [selectedGradeId, setSelectedGradeId] = useState<string>('')
  const [gradeSearchQuery, setGradeSearchQuery] = useState('')
  const [streamSearchQuery, setStreamSearchQuery] = useState('')
  const [showGradeDropdown, setShowGradeDropdown] = useState(false)
  const [showStreamDropdown, setShowStreamDropdown] = useState(false)

  // Stats state
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, byRole: {} as Record<string, number> })

  // Bulk onboard state
  const [bulkUploadMode, setBulkUploadMode] = useState<'manual' | 'csv'>('manual')
  const [bulkStudents, setBulkStudents] = useState<any[]>([])
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [bulkLoading, setBulkLoading] = useState(false)
  
  // Progress tracking for bulk operations
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 })
  const [bulkResults, setBulkResults] = useState<{
    success: Array<{ name: string, email: string }>,
    errors: Array<{ name: string, email: string, error: string }>
  }>({ success: [], errors: [] })
  
  // Drag and drop state
  const [dragActive, setDragActive] = useState(false)

  // Card assignment modal state
  const [showCardAssignModal, setShowCardAssignModal] = useState(false)
  const [cardAssignStudent, setCardAssignStudent] = useState<ApiUser | null>(null)
  const [availableCards, setAvailableCards] = useState<any[]>([])
  const [selectedCardId, setSelectedCardId] = useState<string>('')
  const [cardAssignLoading, setCardAssignLoading] = useState(false)

  // Biometric delete confirmation modal state
  const [showBioDeleteModal, setShowBioDeleteModal] = useState(false)
  const [bioDeleteStudent, setBioDeleteStudent] = useState<ApiUser | null>(null)
  const [bioDeleteLoading, setBioDeleteLoading] = useState(false)

  // PIN reset confirmation modal state
  const [showPinResetModal, setShowPinResetModal] = useState(false)
  const [pinResetStudent, setPinResetStudent] = useState<ApiUser | null>(null)
  const [pinResetLoading, setPinResetLoading] = useState(false)

  const addStudentForm = useForm<AddStudentData>({
    resolver: zodResolver(addStudentSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      countryCode: 'KE',
      studentId: '',
      gradeId: '',
      streamId: '',
      dateOfBirth: '',
      parentEmail: '',
      parentPhone: '',
      roleId: '',
    }
  })

  const bulkStudentForm = useForm<BulkStudentData>({
    resolver: zodResolver(bulkStudentSchema),
    defaultValues: {
      students: [],
      roleId: '',
    }
  })

  const editStudentForm = useForm<EditStudentData>({
    resolver: zodResolver(editStudentSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      countryCode: 'KE',
      studentId: '',
      gradeId: '',
      streamId: '',
      dateOfBirth: '',
      parentEmail: '',
      parentPhone: '',
    }
  })

  const fetchStudents = async () => {
    setLoading(true)
    try {
      // Get all available roles first to identify student role IDs
      const rolesResponse = await getRoles()
      const studentRoleIds = rolesResponse.data
        .filter(role =>
          role.name.toLowerCase().includes('student') ||
          role.name.toLowerCase().includes('pupil') ||
          role.name.toLowerCase().includes('learner')
        )
        .map(role => role.id)

      if (studentRoleIds.length === 0) {
        console.warn('No student roles found')
        setStudents([])
        setLoading(false)
        return
      }

      // Use the first student role ID found
      const primaryStudentRoleId = studentRoleIds[0]

      // Build comprehensive search query
      let combinedSearch = searchQuery || '';

      // Add advanced search terms
      const searchTerms = [
        studentIdSearch && `id:${studentIdSearch}`,
        parentNameSearch && `parent:${parentNameSearch}`,
        teacherSearch && `teacher:${teacherSearch}`
      ].filter(Boolean);

      if (searchTerms.length > 0) {
        combinedSearch = combinedSearch ?
          `${combinedSearch} ${searchTerms.join(' ')}` :
          searchTerms.join(' ');
      }

      // Fetch students with enhanced search and pagination
      const response = await getUsers({
        page: currentPage,
        limit: pageSize,
        search: combinedSearch || undefined,
        roleId: primaryStudentRoleId,
        status: statusFilter !== 'all' ? statusFilter as 'active' | 'inactive' : undefined,
        streamId: streamIdFromUrl || undefined,
        sortBy,
        sortOrder,
      })

      setStudents(response.users)

      // Update pagination info from API response
      if (response.pagination) {
        setCurrentPage(response.pagination.currentPage)
        setTotalPages(response.pagination.totalPages)
        setTotalStudents(response.pagination.totalUsers)
      }

    } catch (error) {
      console.error('Failed to fetch students:', error)
      toast.error('Failed to load students')
    } finally {
      setLoading(false)
    }
  }

  const fetchStudentRoles = async () => {
    try {
      const response = await getRoles()
      // Filter only student-related roles
      const studentRolesList = response.data.filter(role =>
        role.name.toLowerCase().includes('student') ||
        role.name.toLowerCase().includes('pupil') ||
        role.name.toLowerCase().includes('learner')
      )
      setStudentRoles(studentRolesList.map(role => ({ id: role.id, name: role.name })))
    } catch (error) {
      console.error('Failed to fetch student roles:', error)
    }
  }

  const fetchGrades = async () => {
    try {
      const response = await getGrades()
      setGrades(response.grades || [])
    } catch (error) {
      console.error('Failed to fetch grades:', error)
      toast.error('Failed to load grades')
    }
  }

  const fetchStreams = async (gradeId?: string) => {
    try {
      const response = await getStreams({ gradeId })
      setStreams(response)
    } catch (error) {
      console.error('Failed to fetch streams:', error)
      toast.error('Failed to load streams')
    }
  }

  const fetchStudentStats = async () => {
    try {
      const response = await getUserStats()

      // Filter stats to only include student-related counts
      const studentStats = Object.entries(response).reduce((acc, [key, value]) => {
        if (key.toLowerCase().includes('student') ||
            key.toLowerCase().includes('pupil') ||
            key.toLowerCase().includes('learner')) {
          acc.byRole[key] = value as number
        }
        return acc
      }, { total: 0, active: 0, inactive: 0, byRole: {} as Record<string, number> })

      // Calculate totals from student-specific data
      const studentTotal = Object.values(studentStats.byRole).reduce((sum, count) => sum + count, 0)

      setStats({
        total: totalStudents || studentTotal,
        active: students.filter(s => s.status === 'Active').length,
        inactive: students.filter(s => s.status === 'Inactive').length,
        byRole: studentStats.byRole
      })
    } catch (error) {
      console.error('Failed to fetch student stats:', error)
    }
  }

  useEffect(() => {
    fetchStudents()
    fetchStudentRoles()
    fetchGrades()
  }, [currentPage, searchQuery, statusFilter, sortBy, sortOrder, gradeFilter, parentFilter, studentIdSearch, parentNameSearch, teacherSearch, streamIdFromUrl])

  useEffect(() => {
    if (students.length >= 0) {
      fetchStudentStats()
    }
  }, [students])

  // Fetch streams when grade is selected
  useEffect(() => {
    if (selectedGradeId) {
      fetchStreams(selectedGradeId)
      // Reset stream selection when grade changes
      addStudentForm.setValue('streamId', '')
    } else {
      setStreams([])
    }
  }, [selectedGradeId])

  // Debounced search to avoid too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1) // Reset to first page when filters change
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchQuery, statusFilter, gradeFilter])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const getRoleBadge = (user: ApiUser) => {
    const roleName = user.role || 'Student'
    return (
      <span className="badge badge-success">
        {roleName}
      </span>
    )
  }

  const getStatusBadge = (user: ApiUser) => {
    const isActive = user.status === "Active"
    return (
      <span className={`badge ${isActive ? 'badge-success' : 'badge-gray'}`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    )
  }

  const handleViewStudent = (student: ApiUser) => {
    setSelectedStudent(student)
  }

  const handleEditStudent = (student: ApiUser) => {
    setEditingStudent(student)

    // Find the grade for this student's stream
    const studentStream = streams.find(s => s.id === student.streamId || s._id === student.streamId)
    const studentGradeId = studentStream?.gradeId || student.gradeId || ''

    // Set the selected grade for stream filtering
    setSelectedGradeId(studentGradeId)

    // Populate the form with student data
    editStudentForm.reset({
      firstName: student.firstName || '',
      lastName: student.lastName || '',
      email: student.email || '',
      phone: student.phone || '',
      countryCode: (student as any).countryCode || 'KE',
      studentId: student.studentId || '',
      gradeId: studentGradeId,
      streamId: student.streamId || '',
      dateOfBirth: (student as any).dateOfBirth || '',
      parentEmail: (student as any).parentEmail || '',
      parentPhone: (student as any).parentPhone || '',
    })

    // Set search queries to show selected values
    const gradeForStudent = grades.find(g => g.id === studentGradeId || g._id === studentGradeId)
    setGradeSearchQuery(gradeForStudent?.name || '')
    setStreamSearchQuery(studentStream?.name || student.streamName || '')

    setShowEditModal(true)
  }

  const handleSaveEdit = async (data: EditStudentData) => {
    if (!editingStudent) return

    try {
      await updateUser(editingStudent.id, {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        countryCode: data.countryCode,
        studentId: data.studentId,
        gradeId: data.gradeId,
        streamId: data.streamId,
        dateOfBirth: data.dateOfBirth,
        parentEmail: data.parentEmail,
        parentPhone: data.parentPhone,
      })

      toast.success(`${data.firstName} ${data.lastName} updated successfully!`)
      setShowEditModal(false)
      setEditingStudent(null)
      editStudentForm.reset()
      setSelectedGradeId('')
      setGradeSearchQuery('')
      setStreamSearchQuery('')
      fetchStudents()
    } catch (error: any) {
      console.error('Failed to update student:', error)
      const message = error?.response?.data?.message || 'Failed to update student. Please try again.'
      toast.error(message)
    }
  }

  const handleDeleteStudent = async (student: ApiUser) => {
    if (confirm(`Are you sure you want to remove ${student.firstName} ${student.lastName} from students?`)) {
      try {
        await deleteUser(student.id)
        toast.success(`${student.firstName} ${student.lastName} removed successfully`)
        fetchStudents()
      } catch (error) {
        toast.error('Failed to remove student')
      }
    }
  }

  const handleToggleStatus = async (student: ApiUser) => {
    try {
      const newIsActive = student.status === 'Active' ? false : true
      await updateUser(student.id, { isActive: newIsActive })
      toast.success(`${student.firstName} ${student.lastName} ${newIsActive ? 'activated' : 'deactivated'}`)
      fetchStudents()
    } catch (error) {
      toast.error('Failed to update student status')
    }
  }

  // =================== ONBOARDING OPERATIONS ===================

  // Handle opening biometric delete confirmation modal
  const handleDeleteBiometric = (student: ApiUser) => {
    setBioDeleteStudent(student)
    setShowBioDeleteModal(true)
  }

  // Handle confirming biometric deletion
  const handleConfirmDeleteBiometric = async () => {
    if (!bioDeleteStudent) return

    setBioDeleteLoading(true)
    try {
      await deleteBiometric(bioDeleteStudent.id)
      toast.success(`Biometric data deleted for ${bioDeleteStudent.firstName} ${bioDeleteStudent.lastName}`)
      setShowBioDeleteModal(false)
      setBioDeleteStudent(null)
      fetchStudents() // Refresh to update onboarding status
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete biometric data')
    } finally {
      setBioDeleteLoading(false)
    }
  }

  // Handle opening card assignment modal
  const handleAssignCard = async (student: ApiUser) => {
    setCardAssignStudent(student)
    setSelectedCardId('')
    setShowCardAssignModal(true)

    // Fetch available cards
    try {
      const response = await getUnassignedCards()
      // response.data is directly the array of cards
      setAvailableCards(Array.isArray(response.data) ? response.data : [])
    } catch (error) {
      toast.error('Failed to fetch available cards')
      setAvailableCards([])
    }
  }

  // Handle card assignment submission
  const handleCardAssignSubmit = async () => {
    if (!cardAssignStudent || !selectedCardId) {
      toast.error('Please select a card')
      return
    }

    setCardAssignLoading(true)
    try {
      await assignRFIDCard(selectedCardId, {
        assignedToId: cardAssignStudent.id,
        assignedToType: 'student'
      })
      toast.success(`Card assigned to ${cardAssignStudent.firstName} ${cardAssignStudent.lastName}. PIN generated and sent via email.`)
      setShowCardAssignModal(false)
      setCardAssignStudent(null)
      setSelectedCardId('')
      fetchStudents() // Refresh to update onboarding status
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to assign card')
    } finally {
      setCardAssignLoading(false)
    }
  }

  // Handle unassigning card
  const handleUnassignCard = async (student: ApiUser) => {
    if (!confirm(`Are you sure you want to unassign the card from ${student.firstName} ${student.lastName}?`)) {
      return
    }

    // We need the card ID - get it from the onboarding status
    // For now, we'll show an error if no card info is available
    const cardNumber = student.onboardingStatus?.rfid?.cardNumber
    if (!cardNumber) {
      toast.error('No card found for this student')
      return
    }

    try {
      // We need to find the card ID by card number - for now show info message
      toast.error('Please use the RFID Cards management page to unassign cards')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to unassign card')
    }
  }

  // Handle resetting card PIN - opens confirmation modal
  const handleResetCardPIN = (student: ApiUser) => {
    const cardNumber = student.onboardingStatus?.rfid?.cardNumber
    if (!cardNumber) {
      toast.error('No card found for this student')
      return
    }
    setPinResetStudent(student)
    setShowPinResetModal(true)
  }

  // Confirm PIN reset
  const handleConfirmResetPIN = async () => {
    if (!pinResetStudent) return

    const cardNumber = pinResetStudent.onboardingStatus?.rfid?.cardNumber
    if (!cardNumber) {
      toast.error('No card found for this student')
      setShowPinResetModal(false)
      return
    }

    setPinResetLoading(true)
    try {
      // First, get the card ID from the card number
      const cardResult = await getCardByNumber(cardNumber)
      if (!cardResult.data) {
        toast.error('Could not find card details')
        return
      }

      // Now reset the PIN using the card ID
      const result = await resetCardPIN(cardResult.data.id)
      toast.success(result.message || 'PIN reset successfully. New PIN sent to email.')
      setShowPinResetModal(false)
      setPinResetStudent(null)
      // Refresh data
      fetchStudents()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reset PIN')
    } finally {
      setPinResetLoading(false)
    }
  }

  const generateStudentId = () => {
    const prefix = 'STU'
    const year = new Date().getFullYear().toString().slice(-2)
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
    return `${prefix}${year}${random}`
  }

  const handleAddStudent = async (studentData: AddStudentData) => {
    try {
      // Auto-set student role - use the first available student role
      const studentRoleId = studentRoles.length > 0 ? studentRoles[0].id : ''

      if (!studentRoleId) {
        toast.error('No student role found. Please contact administrator.')
        return
      }

      await createUser({
        firstName: studentData.firstName,
        lastName: studentData.lastName,
        email: studentData.email,
        roleId: studentRoleId, // Auto-set behind the scenes
        phone: studentData.phone,
        countryCode: studentData.countryCode,
        studentId: studentData.studentId,
        gradeId: studentData.gradeId,
        streamId: studentData.streamId,
        // institutionId will be set automatically by backend based on authenticated user
        // password will be auto-generated by backend
      })

      toast.success(`${studentData.firstName} ${studentData.lastName} enrolled successfully!`)
      setShowAddModal(false)
      addStudentForm.reset()
      setSelectedGradeId('')
      fetchStudents()

    } catch (error: any) {
      console.error('Failed to add student:', error)
      const message = error?.response?.data?.message || 'Failed to enroll student. Please try again.'
      toast.error(message)
    }
  }

  // Enhanced CSV upload with better parsing and validation
  const handleCSVUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should not exceed 5MB')
      return
    }

    setCsvFile(file)

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string
        const lines = text.split('\n').filter(line => line.trim())

        if (lines.length < 2) {
          toast.error('CSV file should have at least a header row and one data row')
          return
        }

        // Parse header to validate format
        const header = lines[0].toLowerCase().split(',').map(h => h.trim())
        const requiredFields = ['firstname', 'lastname', 'email']
        const missingFields = requiredFields.filter(field => !header.includes(field))
        
        if (missingFields.length > 0) {
          toast.error(`Missing required columns: ${missingFields.join(', ')}`)
          return
        }

        // Parse CSV with proper handling of quoted fields
        const students = lines.slice(1).map((line, index) => {
          try {
            // Simple CSV parsing (handles basic cases)
            const values = line.split(',').map(field => field.trim().replace(/^["']|["']$/g, ''))
            const [firstName, lastName, email, phone, studentId, grade,stream, dateOfBirth, parentEmail, parentPhone] = values

            // Validate required fields
            if (!firstName || !lastName || !email) {
              console.warn(`Row ${index + 2}: Missing required fields`)
              return null
            }

            // Validate email format
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(email)) {
              console.warn(`Row ${index + 2}: Invalid email format: ${email}`)
              return null
            }

            return {
              firstName: firstName || '',
              lastName: lastName || '',
              email: email || '',
              phone: phone || '',
              studentId: studentId || '',
              grade: grade || '',
              stream:stream || "",
              dateOfBirth: dateOfBirth || '',
              parentEmail: parentEmail || '',
              parentPhone: parentPhone || '',
              rowNumber: index + 2
            }
          } catch (error) {
            console.warn(`Error parsing row ${index + 2}:`, error)
            return null
          }
        }).filter(student => student !== null) // Filter out invalid rows

        if (students.length === 0) {
          toast.error('No valid student records found in CSV file')
          return
        }

        setBulkStudents(students)
        bulkStudentForm.setValue('students', students)
        toast.success(`Successfully parsed ${students.length} student records`)

      } catch (error) {
        console.error('CSV parsing error:', error)
        toast.error('Failed to parse CSV file. Please check the format.')
      }
    }
    
    reader.onerror = () => {
      toast.error('Failed to read the CSV file')
    }
    
    reader.readAsText(file)
  }

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const files = e.dataTransfer.files
    if (files && files[0]) {
      const file = files[0]
      if (file.name.toLowerCase().endsWith('.csv')) {
        // Create a synthetic event to reuse handleCSVUpload
        const syntheticEvent = {
          target: { files: [file] }
        } as any
        handleCSVUpload(syntheticEvent)
      } else {
        toast.error('Please drop a CSV file')
      }
    }
  }

  const addManualStudent = () => {
    const newStudent = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      studentId: '',
      grade: '',
      dateOfBirth: '',
      parentEmail: '',
      parentPhone: ''
    }
    const updatedStudents = [...bulkStudents, newStudent]
    setBulkStudents(updatedStudents)
    bulkStudentForm.setValue('students', updatedStudents)
  }

  const removeManualStudent = (index: number) => {
    const updatedStudents = bulkStudents.filter((_, i) => i !== index)
    setBulkStudents(updatedStudents)
    bulkStudentForm.setValue('students', updatedStudents)
  }

  const updateManualStudent = (index: number, field: string, value: string) => {
    const updatedStudents = bulkStudents.map((student, i) =>
      i === index ? { ...student, [field]: value } : student
    )
    setBulkStudents(updatedStudents)
    bulkStudentForm.setValue('students', updatedStudents)
  }

  const handleBulkOnboard = async (data: BulkStudentData) => {
    setBulkLoading(true)
    setBulkProgress({ current: 0, total: data.students.length })
    setBulkResults({ success: [], errors: [] })

    const results: { success: any[]; errors: any[] } = { success: [], errors: [] }

    try {
      // Process students with progress tracking
      for (let i = 0; i < data.students.length; i++) {
        const student = data.students[i]
        setBulkProgress({ current: i + 1, total: data.students.length })

        try {
          // Lookup parent by email or phone if provided
          let parentIds: string[] = []

          if (student.parentEmail || student.parentPhone) {
            // Try email first, then phone
            const lookupResult = await lookupUserByContact({
              email: student.parentEmail || undefined,
              phone: !student.parentEmail ? student.parentPhone : undefined
            })

            if (lookupResult.success && lookupResult.data) {
              parentIds = [lookupResult.data.id]
            } else {
              // Parent not found - log warning but continue with student creation
              console.warn(
                `Parent not found for student ${student.firstName} ${student.lastName}. ` +
                `Searched by: ${student.parentEmail || student.parentPhone}`
              )
            }
          }

          await createUser({
            firstName: student.firstName,
            lastName: student.lastName,
            email: student.email,
            roleId: data.roleId,
            phone: student.phone,
            gradeId: student.grade,
            streamId: student.stream,
            parentIds: parentIds.length > 0 ? parentIds : undefined
          })

          results.success.push({
            name: `${student.firstName} ${student.lastName}`,
            email: student.email,
            parentLinked: parentIds.length > 0
          })

        } catch (error: any) {
          console.error(`Failed to create student ${student.firstName} ${student.lastName}:`, error)

          results.errors.push({
            name: `${student.firstName} ${student.lastName}`,
            email: student.email,
            error: error?.response?.data?.message || error?.message || 'Unknown error'
          })
        }

        // Update results in real-time
        setBulkResults({ ...results })

        // Small delay to prevent overwhelming the server
        if (i < data.students.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      }

      // Show final results
      if (results.success.length > 0) {
        const linkedCount = results.success.filter((s: any) => s.parentLinked).length
        toast.success(
          `Successfully enrolled ${results.success.length} students! ` +
          `${linkedCount} linked to parents.`
        )
      }

      if (results.errors.length > 0) {
        toast.error(`Failed to enroll ${results.errors.length} students. Check the error details below.`)
      }

      // Auto-close modal if all successful
      if (results.errors.length === 0 && results.success.length > 0) {
        setTimeout(() => {
          setShowBulkModal(false)
          setBulkStudents([])
          setCsvFile(null)
          bulkStudentForm.reset()
          setBulkProgress({ current: 0, total: 0 })
          setBulkResults({ success: [], errors: [] })
          fetchStudents()
        }, 2000)
      }

    } catch (error) {
      console.error('Bulk enrollment error:', error)
      toast.error('Failed to process bulk enrollment')
    } finally {
      setBulkLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <UserIcon className="h-8 w-8 text-blue-600 mr-3" />
            Students Management
          </h1>
          <p className="text-gray-600 mt-1">
            {userRole === 'school_admin'
              ? 'Manage students in your institution'
              : 'Manage all students across institutions'
            }
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => {
              // Reset form state - studentId will be auto-generated by backend if left empty
              addStudentForm.setValue('studentId', '')
              setSelectedGradeId('')
              setStreams([])
              setGradeSearchQuery('')
              setStreamSearchQuery('')
              setShowAddModal(true)
            }}
            className="btn btn-primary"
          >
            <UserPlusIcon className="h-4 w-4 mr-2" />
            Enroll Student
          </button>

          <button
            onClick={() => setShowBulkModal(true)}
            className="btn btn-secondary"
          >
            <DocumentArrowUpIcon className="h-4 w-4 mr-2" />
            Bulk Onboard
          </button>
        </div>
      </div>

      {/* Stream Filter Banner - shown when filtering by stream from GradesPage */}
      {streamIdFromUrl && streamNameFromUrl && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AcademicCapIcon className="h-6 w-6 text-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                Viewing students in: <span className="font-bold">{gradeNameFromUrl} - {streamNameFromUrl}</span>
              </p>
              <p className="text-xs text-blue-700">
                Showing {totalStudents} student{totalStudents !== 1 ? 's' : ''} in this stream
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setSearchParams({})
              navigate('/schoolAdmin/students')
            }}
            className="btn btn-sm bg-blue-100 text-blue-700 hover:bg-blue-200"
          >
            <XCircleIcon className="h-4 w-4 mr-1" />
            Clear Filter
          </button>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Students</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total?.toLocaleString() || 0}</p>
            </div>
            <UserIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Students</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active || 0}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-success-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Inactive Students</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inactive || 0}</p>
            </div>
            <XCircleIcon className="h-8 w-8 text-red-500" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">With Parents</p>
              <p className="text-2xl font-bold text-gray-900">
                {students.filter(s => s.hasParents).length}
              </p>
            </div>
            <UsersIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
        <div className="flex-1 relative max-w-md">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search students..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input pl-10"
          />
        </div>

        <div className="flex items-center space-x-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            value={gradeFilter}
            onChange={(e) => setGradeFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Grades</option>
            <option value="Pre-K">Pre-K</option>
            <option value="K">Kindergarten</option>
            <option value="1">Grade 1</option>
            <option value="2">Grade 2</option>
            <option value="3">Grade 3</option>
            <option value="4">Grade 4</option>
            <option value="5">Grade 5</option>
            <option value="6">Grade 6</option>
            <option value="7">Grade 7</option>
            <option value="8">Grade 8</option>
            <option value="9">Grade 9</option>
            <option value="10">Grade 10</option>
            <option value="11">Grade 11</option>
            <option value="12">Grade 12</option>
          </select>

          <select
            value={parentFilter}
            onChange={(e) => setParentFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Students</option>
            <option value="with_parents">With Parents</option>
            <option value="no_parents">No Parents Linked</option>
          </select>

          <button
            onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
            className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              showAdvancedSearch
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Advanced Search
          </button>

          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded text-sm ${viewMode === 'table' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-600'}`}
            >
              Table
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded text-sm ${viewMode === 'grid' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-600'}`}
            >
              Grid
            </button>
          </div>
        </div>
      </div>

      {/* Advanced Search Panel */}
      {showAdvancedSearch && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <h3 className="text-lg font-semibold text-blue-900 mb-4">Advanced Search</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Student ID</label>
              <input
                type="text"
                placeholder="Search by Student ID..."
                value={studentIdSearch}
                onChange={(e) => setStudentIdSearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Parent Name</label>
              <input
                type="text"
                placeholder="Search by Parent Name..."
                value={parentNameSearch}
                onChange={(e) => setParentNameSearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Class Teacher</label>
              <input
                type="text"
                placeholder="Search by Teacher Name..."
                value={teacherSearch}
                onChange={(e) => setTeacherSearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-blue-200">
            <div className="text-sm text-blue-700">
              💡 Search across student IDs, parent names, and class teachers for precise results
            </div>
            <button
              onClick={() => {
                setStudentIdSearch('')
                setParentNameSearch('')
                setTeacherSearch('')
                setSearchQuery('')
                setStatusFilter('all')
                setGradeFilter('all')
                setParentFilter('all')
              }}
              className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800"
            >
              Clear All Filters
            </button>
          </div>
        </motion.div>
      )}

      {/* Results Count */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalStudents)} of {totalStudents} students</span>
        <div className="flex items-center space-x-3">
          <span className="text-xs text-gray-500">
            {userRole === 'school_admin' ? 'Students in your institution' : 'All students'}
          </span>
        </div>
      </div>

      {/* Students Table */}
      {viewMode === 'table' && (
        <div className="dashboard-card overflow-visible">
          <div className="overflow-x-auto overflow-y-visible">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Academic Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Parent Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Check-In Methods
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Enrolled
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {students.map((student) => (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-purple-200 rounded-full flex items-center justify-center mr-3">
                          <UserIcon className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {student.firstName} {student.lastName}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center space-x-1">
                            <span>{student.email}</span>
                          </div>
                          {student.studentId && (
                            <div className="text-xs text-blue-600 font-mono">
                              ID: {student.studentId}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {student.academicSummary ? (
                          <>
                            <div className="flex items-center space-x-2">
                              <BookOpenIcon className="h-4 w-4 text-green-600" />
                              <span className="text-sm font-medium text-gray-900">
                                {student.academicSummary.fullClassInfo || 'Not Assigned'}
                              </span>
                            </div>
                            {student.academicSummary.classTeacher && (
                              <div className="flex items-center space-x-2">
                                <AcademicCapIcon className="h-4 w-4 text-purple-600" />
                                <span className="text-xs text-gray-600">
                                  Teacher: {student.academicSummary.classTeacher}
                                </span>
                              </div>
                            )}
                            <div className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              student.academicSummary.academicStatus === 'Fully Enrolled'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {student.academicSummary.academicStatus}
                            </div>
                          </>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <XCircleIcon className="h-4 w-4 text-red-500" />
                            <span className="text-sm text-red-600">No Academic Assignment</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {student.parentNames && student.parentNames.length > 0 ? (
                          <>
                            <div className="flex items-center space-x-2">
                              <UsersIcon className="h-4 w-4 text-blue-600" />
                              <span className="text-sm font-medium text-gray-900">
                                {student.parentNames.join(', ')}
                              </span>
                            </div>
                            {student.parentContacts && student.parentContacts.length > 0 && (
                              <div className="flex items-center space-x-2">
                                <PhoneIcon className="h-4 w-4 text-green-600" />
                                <span className="text-xs text-gray-600">
                                  {student.parentContacts[0].phone}
                                </span>
                              </div>
                            )}
                            <div className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {student.familySummary?.parentCount || 0} Parent(s)
                            </div>
                          </>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <XCircleIcon className="h-4 w-4 text-orange-500" />
                            <span className="text-sm text-orange-600">No Parent Linked</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(student)}
                    </td>
                    {/* Check-In Methods Column */}
                    <td className="px-6 py-4 whitespace-nowrap overflow-visible">
                      <div className="flex items-center space-x-2">
                        {/* Biometric Status */}
                        <Menu as="div" className="relative inline-block text-left">
                          <Menu.Button
                            className={`flex items-center px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                              student.onboardingStatus?.hasBiometric
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                            title={student.onboardingStatus?.hasBiometric ? 'Biometric enrolled' : 'Biometric not enrolled'}
                          >
                            <FingerPrintIcon className="h-4 w-4 mr-1" />
                            Bio
                          </Menu.Button>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-150"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items className="absolute left-0 z-[100] mt-2 w-72 origin-top-left rounded-lg bg-white shadow-xl ring-1 ring-black ring-opacity-10 focus:outline-none border border-gray-100">
                              <div className="p-2">
                                <div className="px-3 py-2 text-xs font-semibold text-gray-900 uppercase tracking-wide border-b border-gray-100 mb-2">
                                  Biometric Status
                                </div>
                                {student.onboardingStatus?.hasBiometric ? (
                                  <>
                                    <div className="px-3 py-2 text-sm text-gray-600 bg-green-50 rounded-md mb-2 whitespace-normal">
                                      <div className="flex items-start">
                                        <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                        <span>{student.onboardingStatus.biometric.fingerPrintCount} fingerprint(s) enrolled</span>
                                      </div>
                                    </div>
                                    <Menu.Item>
                                      {({ active }) => (
                                        <button
                                          onClick={() => handleDeleteBiometric(student)}
                                          className={`${active ? 'bg-red-50 text-red-700' : 'text-red-600'} flex w-full items-center px-3 py-2 text-sm rounded-md transition-colors`}
                                        >
                                          <TrashIcon className="h-4 w-4 mr-2" />
                                          Delete Biometric
                                        </button>
                                      )}
                                    </Menu.Item>
                                  </>
                                ) : (
                                  <div className="px-3 py-2 text-sm text-gray-500 bg-gray-50 rounded-md whitespace-normal">
                                    <div className="flex items-start">
                                      <XCircleIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                                      <span>Not enrolled. Use biometric device to register.</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </Menu.Items>
                          </Transition>
                        </Menu>

                        {/* RFID Card Status */}
                        <Menu as="div" className="relative inline-block text-left">
                          <Menu.Button
                            className={`flex items-center px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                              student.onboardingStatus?.hasRfid
                                ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                            title={student.onboardingStatus?.hasRfid ? `Card: ${student.onboardingStatus.rfid.cardNumber}` : 'No RFID card assigned'}
                          >
                            <CreditCardIcon className="h-4 w-4 mr-1" />
                            RFID
                          </Menu.Button>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-150"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items className="absolute left-0 z-[100] mt-2 w-64 origin-top-left rounded-lg bg-white shadow-xl ring-1 ring-black ring-opacity-10 focus:outline-none border border-gray-100">
                              <div className="p-2">
                                <div className="px-3 py-2 text-xs font-semibold text-gray-900 uppercase tracking-wide border-b border-gray-100 mb-2">
                                  RFID Card Status
                                </div>
                                {student.onboardingStatus?.hasRfid ? (
                                  <>
                                    <div className="px-3 py-2 text-sm bg-blue-50 rounded-md mb-2 space-y-1">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Card:</span>
                                        <span className="font-medium text-gray-900">{student.onboardingStatus.rfid.cardNumber}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Type:</span>
                                        <span className="font-medium text-gray-900">{student.onboardingStatus.rfid.cardType}</span>
                                      </div>
                                      <div className="flex justify-between items-center">
                                        <span className="text-gray-600">PIN:</span>
                                        {student.onboardingStatus.rfid.hasPinSet ? (
                                          <span className="text-green-600 flex items-center font-medium">
                                            <CheckCircleIcon className="h-4 w-4 mr-1" /> Set
                                          </span>
                                        ) : (
                                          <span className="text-orange-600 flex items-center font-medium">
                                            <XCircleIcon className="h-4 w-4 mr-1" /> Not set
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="border-t border-gray-100 pt-2 space-y-1">
                                      <Menu.Item>
                                        {({ active }) => (
                                          <button
                                            onClick={() => handleResetCardPIN(student)}
                                            className={`${active ? 'bg-gray-100' : ''} flex w-full items-center px-3 py-2 text-sm text-gray-700 rounded-md transition-colors`}
                                          >
                                            <KeyIcon className="h-4 w-4 mr-2" />
                                            Reset PIN
                                          </button>
                                        )}
                                      </Menu.Item>
                                      <Menu.Item>
                                        {({ active }) => (
                                          <button
                                            onClick={() => handleUnassignCard(student)}
                                            className={`${active ? 'bg-red-50 text-red-700' : 'text-red-600'} flex w-full items-center px-3 py-2 text-sm rounded-md transition-colors`}
                                          >
                                            <XCircleIcon className="h-4 w-4 mr-2" />
                                            Unassign Card
                                          </button>
                                        )}
                                      </Menu.Item>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="px-3 py-2 text-sm text-gray-500 bg-gray-50 rounded-md mb-2">
                                      No card assigned
                                    </div>
                                    <Menu.Item>
                                      {({ active }) => (
                                        <button
                                          onClick={() => handleAssignCard(student)}
                                          className={`${active ? 'bg-blue-100 text-blue-800' : 'text-blue-600'} flex w-full items-center px-3 py-2 text-sm rounded-md transition-colors font-medium`}
                                        >
                                          <CreditCardIcon className="h-4 w-4 mr-2" />
                                          Assign Card
                                        </button>
                                      )}
                                    </Menu.Item>
                                  </>
                                )}
                              </div>
                            </Menu.Items>
                          </Transition>
                        </Menu>

                        {/* Smartwatch Status (wristband type RFID) */}
                        <Menu as="div" className="relative inline-block text-left">
                          <Menu.Button
                            className={`flex items-center px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                              student.onboardingStatus?.hasSmartwatch
                                ? 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                            title={student.onboardingStatus?.hasSmartwatch ? 'Smartwatch paired' : 'No smartwatch'}
                          >
                            <DevicePhoneMobileIcon className="h-4 w-4 mr-1" />
                            Watch
                          </Menu.Button>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-150"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items className="absolute right-0 z-[100] mt-2 w-72 origin-top-right rounded-lg bg-white shadow-xl ring-1 ring-black ring-opacity-10 focus:outline-none border border-gray-100">
                              <div className="p-2">
                                <div className="px-3 py-2 text-xs font-semibold text-gray-900 uppercase tracking-wide border-b border-gray-100 mb-2">
                                  Smartwatch Status
                                </div>
                                {student.onboardingStatus?.hasSmartwatch ? (
                                  <div className="px-3 py-2 text-sm bg-purple-50 rounded-md">
                                    <CheckCircleIcon className="h-4 w-4 inline text-purple-500 mr-2" />
                                    Smartwatch paired
                                    {student.onboardingStatus.smartwatch.batteryLevel !== null && (
                                      <div className="mt-2 flex items-center text-purple-700">
                                        <BoltIcon className="h-4 w-4 mr-1" />
                                        Battery: {student.onboardingStatus.smartwatch.batteryLevel}%
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="px-3 py-2 text-sm text-gray-500 bg-gray-50 rounded-md whitespace-normal">
                                    <div className="flex items-start">
                                      <XCircleIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                                      <span>No smartwatch paired. Assign a wristband card to enable.</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </Menu.Items>
                          </Transition>
                        </Menu>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(student.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewStudent(student)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditStudent(student)}
                          className="text-gray-600 hover:text-gray-900"
                          title="Edit Student"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(student)}
                          className={student.status === 'Active' ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}
                          title={student.status === 'Active' ? 'Deactivate' : 'Activate'}
                        >
                          {student.status === 'Active' ? (
                            <LockClosedIcon className="h-4 w-4" />
                          ) : (
                            <LockOpenIcon className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteStudent(student)}
                          className="text-red-600 hover:text-red-900"
                          title="Remove Student"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Enhanced Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {students.map((student) => (
            <motion.div
              key={student.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                {/* Student Header */}
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-200 rounded-full flex items-center justify-center">
                    <UserIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {student.firstName} {student.lastName}
                    </h3>
                    {student.studentId && (
                      <div className="text-sm text-blue-600 font-mono">
                        ID: {student.studentId}
                      </div>
                    )}
                  </div>
                  <div className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    student.status === 'Active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {student.status}
                  </div>
                </div>

                {/* Academic Information */}
                <div className="space-y-3 mb-4">
                  <div className="border-l-4 border-green-400 pl-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <BookOpenIcon className="h-4 w-4 mr-2 text-green-600" />
                      Academic Assignment
                    </h4>
                    {student.academicSummary ? (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-sm font-medium text-gray-900">
                            {student.academicSummary.fullClassInfo}
                          </span>
                        </div>
                        {student.academicSummary.classTeacher && (
                          <div className="flex items-center space-x-2">
                            <AcademicCapIcon className="h-4 w-4 text-purple-500" />
                            <span className="text-xs text-gray-600">
                              Teacher: {student.academicSummary.classTeacher}
                            </span>
                          </div>
                        )}
                        <div className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                          student.academicSummary.academicStatus === 'Fully Enrolled'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {student.academicSummary.academicStatus}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 text-red-600">
                        <XCircleIcon className="h-4 w-4" />
                        <span className="text-sm">Not Assigned to Grade/Stream</span>
                      </div>
                    )}
                  </div>

                  {/* Parent Information */}
                  <div className="border-l-4 border-blue-400 pl-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <UsersIcon className="h-4 w-4 mr-2 text-blue-600" />
                      Family Links
                    </h4>
                    {student.parentNames && student.parentNames.length > 0 ? (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-sm font-medium text-gray-900">
                            {student.parentNames.join(', ')}
                          </span>
                        </div>
                        {student.parentContacts && student.parentContacts.length > 0 && (
                          <div className="flex items-center space-x-2">
                            <PhoneIcon className="h-4 w-4 text-green-500" />
                            <span className="text-xs text-gray-600">
                              {student.parentContacts[0].phone}
                            </span>
                          </div>
                        )}
                        <div className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {student.familySummary?.parentCount || 0} Parent(s) Linked
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 text-orange-600">
                        <XCircleIcon className="h-4 w-4" />
                        <span className="text-sm">No Parent Linked</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact Information */}
                <div className="border-t pt-4 mb-4">
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <EnvelopeIcon className="h-4 w-4" />
                      <span className="truncate">{student.email}</span>
                    </div>
                    {student.phone && (
                      <div className="flex items-center space-x-1">
                        <PhoneIcon className="h-4 w-4" />
                        <span>{student.phone}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="text-xs text-gray-500">
                    Created: {formatDate(student.createdAt)}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleViewStudent(student)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEditStudent(student)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Edit Student"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleToggleStatus(student)}
                      className={`p-2 rounded-lg transition-colors ${
                        student.status === 'Active'
                          ? 'text-red-600 hover:bg-red-50'
                          : 'text-green-600 hover:bg-green-50'
                      }`}
                      title={student.status === 'Active' ? 'Deactivate' : 'Activate'}
                    >
                      {student.status === 'Active' ? (
                        <LockClosedIcon className="h-4 w-4" />
                      ) : (
                        <LockOpenIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {students.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card"
        >
          <div className="text-center py-12">
            <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No students found</h3>
            <p className="mt-2 text-gray-500">
              {searchQuery || statusFilter !== 'all' || gradeFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : userRole === 'school_admin'
                  ? 'Get started by enrolling your first student.'
                  : 'No students found in the system.'
              }
            </p>
            {(!searchQuery && statusFilter === 'all' && gradeFilter === 'all') && (
              <button
                onClick={() => {
                  addStudentForm.setValue('studentId', '')
                  setSelectedGradeId('')
                  setStreams([])
                  setGradeSearchQuery('')
                  setStreamSearchQuery('')
                  setShowAddModal(true)
                }}
                className="mt-4 btn btn-primary"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Enroll First Student
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* Pagination Controls */}
      {totalStudents > pageSize && (
        <div className="flex items-center justify-between bg-white px-6 py-4 border border-gray-200 rounded-lg">
          <div className="flex items-center text-sm text-gray-700">
            <span>
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalStudents)} of {totalStudents} students
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = i + 1;
                if (totalPages <= 5) {
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                }

                // For more than 5 pages, show smart pagination
                const start = Math.max(1, currentPage - 2);
                const end = Math.min(totalPages, currentPage + 2);

                if (i === 0 && start > 1) {
                  return (
                    <React.Fragment key="start">
                      <button
                        onClick={() => setCurrentPage(1)}
                        className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        1
                      </button>
                      {start > 2 && <span className="px-2 text-gray-500">...</span>}
                    </React.Fragment>
                  );
                }

                if (page >= start && page <= end) {
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                }

                if (i === 4 && end < totalPages) {
                  return (
                    <React.Fragment key="end">
                      {end < totalPages - 1 && <span className="px-2 text-gray-500">...</span>}
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        {totalPages}
                      </button>
                    </React.Fragment>
                  );
                }

                return null;
              })}
            </div>

            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add Student Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Enroll New Student</h3>
                    <p className="text-sm text-gray-500">Add a new student to the institution</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowAddModal(false)
                      setSelectedGradeId('')
                      setStreams([])
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={addStudentForm.handleSubmit(handleAddStudent)} className="p-6 space-y-6" autoComplete="off">
                {/* Hidden fields to prevent autofill */}
                <input type="text" style={{display:'none'}} />
                <input type="password" style={{display:'none'}} />

                {/* Student Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <UserIcon className="h-5 w-5 text-blue-600 mr-2" />
                    Student Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        {...addStudentForm.register('firstName')}
                        placeholder="e.g., Jane"
                        error={addStudentForm.formState.errors.firstName?.message}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        {...addStudentForm.register('lastName')}
                        placeholder="e.g., Smith"
                        error={addStudentForm.formState.errors.lastName?.message}
                      />
                    </div>

                    <Input
                      {...addStudentForm.register('studentId')}
                      label="Student ID (Optional)"
                      placeholder="Leave empty for auto-generation"
                      error={addStudentForm.formState.errors.studentId?.message}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <Input
                        {...addStudentForm.register('email')}
                        type="email"
                        placeholder="e.g., jane.smith@students.school.edu"
                        autoComplete="off"
                        data-lpignore="true"
                        error={addStudentForm.formState.errors.email?.message}
                      />
                    </div>

                    {/* Password is auto-generated by backend */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <LockClosedIcon className="h-5 w-5 text-blue-600 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-blue-800">Secure Password</p>
                          <p className="text-xs text-blue-600">Auto-generated and sent via email</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone (Optional)
                      </label>
                      <PhoneNumberInput
                        value={addStudentForm.watch('phone') || ''}
                        countryCode={addStudentForm.watch('countryCode') || 'KE'}
                        onChange={(phone, countryCode) => {
                          addStudentForm.setValue('phone', phone)
                          addStudentForm.setValue('countryCode', countryCode)
                          addStudentForm.trigger(['phone', 'countryCode'])
                        }}
                        placeholder="Enter phone number"
                        error={addStudentForm.formState.errors.phone?.message}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date of Birth <span className="text-red-500">*</span>
                      </label>
                      <Input
                        {...addStudentForm.register('dateOfBirth')}
                        type="date"
                        max={new Date().toISOString().split('T')[0]}
                        error={addStudentForm.formState.errors.dateOfBirth?.message}
                      />
                    </div>

                    {/* Grade Selection with Search */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Grade <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search and select grade..."
                          value={gradeSearchQuery}
                          onChange={(e) => {
                            setGradeSearchQuery(e.target.value)
                            setShowGradeDropdown(true)
                          }}
                          onFocus={() => setShowGradeDropdown(true)}
                          onBlur={() => setTimeout(() => setShowGradeDropdown(false), 150)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {showGradeDropdown && (
                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {grades
                              .filter(g => !gradeSearchQuery || g.name.toLowerCase().includes(gradeSearchQuery.toLowerCase()))
                              .map(grade => (
                                <button
                                  key={grade.id}
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault()
                                    addStudentForm.setValue('gradeId', grade.id)
                                    addStudentForm.setValue('streamId', '')
                                    setSelectedGradeId(grade.id)
                                    setGradeSearchQuery(grade.name)
                                    setStreamSearchQuery('')
                                    setShowGradeDropdown(false)
                                  }}
                                  className="w-full px-4 py-2 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="font-medium">{grade.name}</div>
                                  {grade.description && (
                                    <div className="text-xs text-gray-500">{grade.description}</div>
                                  )}
                                </button>
                              ))}
                            {grades.filter(g => !gradeSearchQuery || g.name.toLowerCase().includes(gradeSearchQuery.toLowerCase())).length === 0 && (
                              <div className="px-4 py-2 text-gray-500 text-sm">No grades found</div>
                            )}
                          </div>
                        )}
                      </div>
                      {addStudentForm.formState.errors.gradeId && (
                        <p className="mt-1 text-sm text-red-600">{addStudentForm.formState.errors.gradeId.message}</p>
                      )}
                    </div>

                    {/* Stream Selection with Search */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Stream <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder={selectedGradeId ? "Search and select stream..." : "Select grade first"}
                          value={streamSearchQuery}
                          onChange={(e) => {
                            setStreamSearchQuery(e.target.value)
                            setShowStreamDropdown(true)
                          }}
                          onFocus={() => selectedGradeId && setShowStreamDropdown(true)}
                          onBlur={() => setTimeout(() => setShowStreamDropdown(false), 150)}
                          disabled={!selectedGradeId}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                        {showStreamDropdown && selectedGradeId && (
                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {streams
                              .filter(s => !streamSearchQuery || s.name.toLowerCase().includes(streamSearchQuery.toLowerCase()))
                              .map(stream => (
                                <button
                                  key={stream.id}
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault()
                                    addStudentForm.setValue('streamId', stream.id)
                                    setStreamSearchQuery(stream.name)
                                    setShowStreamDropdown(false)
                                  }}
                                  className="w-full px-4 py-2 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="font-medium">{stream.name}</div>
                                  {stream.classTeacher && (
                                    <div className="text-xs text-gray-500">Teacher: {stream.classTeacher}</div>
                                  )}
                                </button>
                              ))}
                            {streams.filter(s => !streamSearchQuery || s.name.toLowerCase().includes(streamSearchQuery.toLowerCase())).length === 0 && (
                              <div className="px-4 py-2 text-gray-500 text-sm">
                                {streams.length === 0 ? 'No streams for this grade' : 'No streams found'}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {addStudentForm.formState.errors.streamId && (
                        <p className="mt-1 text-sm text-red-600">{addStudentForm.formState.errors.streamId.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Parent/Guardian Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <UsersIcon className="h-5 w-5 text-green-600 mr-2" />
                    Parent/Guardian Information (Optional)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      {...addStudentForm.register('parentEmail')}
                      type="email"
                      label="Parent Email"
                      placeholder="e.g., parent@example.com"
                      autoComplete="off"
                      data-lpignore="true"
                      error={addStudentForm.formState.errors.parentEmail?.message}
                    />

                    <Input
                      {...addStudentForm.register('parentPhone')}
                      label="Parent Phone"
                      placeholder="e.g., +254 712 345 678"
                      error={addStudentForm.formState.errors.parentPhone?.message}
                    />
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-blue-900">Student Enrollment Process</h5>
                      <div className="text-sm text-blue-700 mt-1 space-y-1">
                        <p>• Student will be enrolled in your institution automatically</p>
                        <p>• RFID card can be assigned after enrollment</p>
                        <p>• Welcome email with login credentials will be sent</p>
                        <p>• Parent will be notified if contact information is provided</p>
                        <p>• Student account will be activated immediately</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false)
                      setSelectedGradeId('')
                      setStreams([])
                    }}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <UserIcon className="h-4 w-4 mr-2" />
                    Enroll Student
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Student Modal */}
      <AnimatePresence>
        {showEditModal && editingStudent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowEditModal(false)
              setEditingStudent(null)
              setSelectedGradeId('')
              setGradeSearchQuery('')
              setStreamSearchQuery('')
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center">
                  <PencilIcon className="h-6 w-6 text-blue-600 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-900">Edit Student</h2>
                </div>
                <button
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingStudent(null)
                    setSelectedGradeId('')
                    setGradeSearchQuery('')
                    setStreamSearchQuery('')
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={editStudentForm.handleSubmit(handleSaveEdit)} className="p-6 space-y-6">
                {/* Student Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <UserIcon className="h-5 w-5 text-blue-600 mr-2" />
                    Student Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        {...editStudentForm.register('firstName')}
                        placeholder="e.g., Jane"
                        error={editStudentForm.formState.errors.firstName?.message}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        {...editStudentForm.register('lastName')}
                        placeholder="e.g., Smith"
                        error={editStudentForm.formState.errors.lastName?.message}
                      />
                    </div>

                    <Input
                      {...editStudentForm.register('studentId')}
                      label="Student ID"
                      placeholder="Student ID"
                      error={editStudentForm.formState.errors.studentId?.message}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <Input
                        {...editStudentForm.register('email')}
                        type="email"
                        placeholder="e.g., jane.smith@students.school.edu"
                        error={editStudentForm.formState.errors.email?.message}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone (Optional)
                      </label>
                      <PhoneNumberInput
                        value={editStudentForm.watch('phone') || ''}
                        countryCode={editStudentForm.watch('countryCode') || 'KE'}
                        onChange={(phone, countryCode) => {
                          editStudentForm.setValue('phone', phone)
                          editStudentForm.setValue('countryCode', countryCode)
                        }}
                        placeholder="Enter phone number"
                        error={editStudentForm.formState.errors.phone?.message}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date of Birth
                      </label>
                      <Input
                        {...editStudentForm.register('dateOfBirth')}
                        type="date"
                        max={new Date().toISOString().split('T')[0]}
                        error={editStudentForm.formState.errors.dateOfBirth?.message}
                      />
                    </div>
                  </div>
                </div>

                {/* Academic Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <AcademicCapIcon className="h-5 w-5 text-purple-600 mr-2" />
                    Academic Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Grade Selection with Search */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Grade <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search and select grade..."
                          value={gradeSearchQuery}
                          onChange={(e) => {
                            setGradeSearchQuery(e.target.value)
                            setShowGradeDropdown(true)
                          }}
                          onFocus={() => setShowGradeDropdown(true)}
                          onBlur={() => setTimeout(() => setShowGradeDropdown(false), 150)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {showGradeDropdown && (
                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {grades
                              .filter(g => !gradeSearchQuery || g.name.toLowerCase().includes(gradeSearchQuery.toLowerCase()))
                              .map(grade => (
                                <button
                                  key={grade.id}
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault()
                                    editStudentForm.setValue('gradeId', grade.id)
                                    editStudentForm.setValue('streamId', '')
                                    setSelectedGradeId(grade.id)
                                    setGradeSearchQuery(grade.name)
                                    setStreamSearchQuery('')
                                    setShowGradeDropdown(false)
                                  }}
                                  className="w-full px-4 py-2 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="font-medium">{grade.name}</div>
                                </button>
                              ))}
                          </div>
                        )}
                      </div>
                      {editStudentForm.formState.errors.gradeId && (
                        <p className="mt-1 text-sm text-red-600">{editStudentForm.formState.errors.gradeId.message}</p>
                      )}
                    </div>

                    {/* Stream Selection with Search */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Stream <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder={selectedGradeId ? "Search and select stream..." : "Select grade first"}
                          value={streamSearchQuery}
                          onChange={(e) => {
                            setStreamSearchQuery(e.target.value)
                            setShowStreamDropdown(true)
                          }}
                          onFocus={() => selectedGradeId && setShowStreamDropdown(true)}
                          onBlur={() => setTimeout(() => setShowStreamDropdown(false), 150)}
                          disabled={!selectedGradeId}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                        {showStreamDropdown && selectedGradeId && (
                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {streams
                              .filter(s => !streamSearchQuery || s.name.toLowerCase().includes(streamSearchQuery.toLowerCase()))
                              .map(stream => (
                                <button
                                  key={stream.id}
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault()
                                    editStudentForm.setValue('streamId', stream.id)
                                    setStreamSearchQuery(stream.name)
                                    setShowStreamDropdown(false)
                                  }}
                                  className="w-full px-4 py-2 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="font-medium">{stream.name}</div>
                                </button>
                              ))}
                          </div>
                        )}
                      </div>
                      {editStudentForm.formState.errors.streamId && (
                        <p className="mt-1 text-sm text-red-600">{editStudentForm.formState.errors.streamId.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Parent/Guardian Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <UsersIcon className="h-5 w-5 text-green-600 mr-2" />
                    Parent/Guardian Information (Optional)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      {...editStudentForm.register('parentEmail')}
                      type="email"
                      label="Parent Email"
                      placeholder="e.g., parent@example.com"
                      error={editStudentForm.formState.errors.parentEmail?.message}
                    />

                    <Input
                      {...editStudentForm.register('parentPhone')}
                      label="Parent Phone"
                      placeholder="e.g., +254 712 345 678"
                      error={editStudentForm.formState.errors.parentPhone?.message}
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false)
                      setEditingStudent(null)
                      setSelectedGradeId('')
                      setGradeSearchQuery('')
                      setStreamSearchQuery('')
                    }}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Student Details Modal */}
      <AnimatePresence>
        {selectedStudent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedStudent(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-200 rounded-full flex items-center justify-center">
                      <UserIcon className="h-10 w-10 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {selectedStudent.firstName} {selectedStudent.lastName}
                      </h3>
                      <p className="text-sm text-gray-500">{selectedStudent.institution || 'No Institution'}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedStudent(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Contact Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Contact Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{selectedStudent.email}</span>
                      </div>
                      {selectedStudent.phone && (
                        <div className="flex items-center space-x-3">
                          <PhoneIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{selectedStudent.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Account Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Student Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Role</span>
                        <div className="flex items-center space-x-2">
                          <UserIcon className="h-4 w-4 text-blue-600" />
                          {getRoleBadge(selectedStudent)}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        {getStatusBadge(selectedStudent)}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Enrolled</span>
                        <span className="text-sm text-gray-900">{formatDate(selectedStudent.createdAt)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Created By</span>
                        <span className="text-sm text-gray-900">{selectedStudent.createdBy || 'System'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
                <div className="flex justify-end space-x-3">
                  <Button
                    onClick={() => toast.success('RFID card assignment coming soon!')}
                    variant="secondary"
                  >
                    <CreditCardIcon className="h-4 w-4 mr-2" />
                    Assign RFID Card
                  </Button>
                  <Button
                    onClick={() => handleEditStudent(selectedStudent)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit Student
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Onboard Modal */}
      <AnimatePresence>
        {showBulkModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowBulkModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <DocumentArrowUpIcon className="h-6 w-6 text-blue-600 mr-2" />
                      Bulk Student Onboard
                    </h3>
                    <p className="text-sm text-gray-500">Enroll multiple students at once</p>
                  </div>
                  <button
                    onClick={() => setShowBulkModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                <form onSubmit={bulkStudentForm.handleSubmit(handleBulkOnboard)} className="space-y-6">

                  {/* Mode Selection */}
                  <div className="border-b border-gray-200 pb-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-4">Select Upload Method</h4>
                    <div className="flex space-x-4">
                      <button
                        type="button"
                        onClick={() => setBulkUploadMode('manual')}
                        className={`flex-1 p-4 border rounded-lg text-center transition-colors ${
                          bulkUploadMode === 'manual'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <TableCellsIcon className="h-8 w-8 mx-auto mb-2" />
                        <div className="font-medium">Manual Entry</div>
                        <div className="text-sm text-gray-500">Add students one by one</div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setBulkUploadMode('csv')}
                        className={`flex-1 p-4 border rounded-lg text-center transition-colors ${
                          bulkUploadMode === 'csv'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <DocumentArrowUpIcon className="h-8 w-8 mx-auto mb-2" />
                        <div className="font-medium">CSV Upload</div>
                        <div className="text-sm text-gray-500">Upload from CSV file</div>
                      </button>
                    </div>
                  </div>

                  {/* Role Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Student Role *
                    </label>
                    <select
                      {...bulkStudentForm.register('roleId')}
                      className="form-select w-full"
                      required
                    >
                      <option value="">Select student role...</option>
                      {studentRoles.map(role => (
                        <option key={role.id} value={role.id}>{role.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* CSV Upload Mode */}
                  {bulkUploadMode === 'csv' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Upload CSV File
                        </label>
                        <div className="flex items-center justify-center w-full">
                          <label 
                            className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
                              dragActive 
                                ? 'border-blue-500 bg-blue-50' 
                                : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                            }`}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                          >
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <DocumentArrowUpIcon className={`w-8 h-8 mb-4 ${dragActive ? 'text-blue-500' : 'text-gray-500'}`} />
                              <p className={`mb-2 text-sm ${dragActive ? 'text-blue-700' : 'text-gray-500'}`}>
                                <span className="font-semibold">
                                  {dragActive ? 'Drop CSV file here' : 'Click to upload or drag and drop'}
                                </span>
                              </p>
                              <p className={`text-xs ${dragActive ? 'text-blue-600' : 'text-gray-500'}`}>
                                CSV files only (max 5MB)
                              </p>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept=".csv"
                              onChange={handleCSVUpload}
                            />
                          </label>
                        </div>
                        {csvFile && (
                          <p className="mt-2 text-sm text-green-600">
                            ✓ {csvFile.name} uploaded ({bulkStudents.length} students found)
                          </p>
                        )}
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h5 className="text-sm font-medium text-blue-900 mb-3">📋 CSV Format Requirements</h5>

                        <div className="space-y-3">
                          <div>
                            <p className="text-sm text-blue-700 mb-2">
                              <strong>Column headers</strong> (first row):
                            </p>
                            <div className="text-xs text-blue-700 font-mono bg-blue-100 p-2 rounded overflow-x-auto">
                              firstName,lastName,email,phone,studentId,grade,stream,dateOfBirth,parentEmail,parentPhone
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-blue-700 mb-1"><strong>Required fields:</strong></p>
                              <ul className="text-xs text-blue-600 space-y-1 ml-4">
                                <li>• firstName, lastName, email</li>
                                <li>• grade (name or ID)</li>
                                <li>• Valid email format required</li>
                              </ul>
                            </div>
                            <div>
                              <p className="text-sm text-blue-700 mb-1"><strong>Optional fields:</strong></p>
                              <ul className="text-xs text-blue-600 space-y-1 ml-4">
                                <li>• phone - student phone number</li>
                                <li>• studentId - auto-generated if empty</li>
                                <li>• stream - stream name or ID</li>
                                <li>• dateOfBirth - YYYY-MM-DD format</li>
                                <li>• parentEmail - links to existing parent</li>
                                <li>• parentPhone - links to existing parent</li>
                              </ul>
                            </div>
                          </div>

                          <div>
                            <p className="text-sm text-blue-700 mb-1"><strong>File limits:</strong></p>
                            <ul className="text-xs text-blue-600 space-y-1 ml-4">
                              <li>• Maximum file size: 5MB</li>
                              <li>• CSV format only</li>
                              <li>• UTF-8 encoding recommended</li>
                            </ul>
                          </div>
                        </div>

                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-blue-200">
                          <a
                            href="/sample-students.csv"
                            download="sample-students.csv"
                            className="text-sm text-blue-600 hover:text-blue-800 underline font-medium"
                          >
                            📄 Download sample CSV template
                          </a>
                          <span className="text-xs text-blue-500">
                            ✨ Drag & drop supported
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Manual Entry Mode */}
                  {bulkUploadMode === 'manual' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h5 className="text-md font-medium text-gray-900">Students Information</h5>
                        <Button
                          type="button"
                          onClick={addManualStudent}
                          variant="secondary"
                          size="sm"
                        >
                          <PlusIcon className="h-4 w-4 mr-1" />
                          Add Student
                        </Button>
                      </div>

                      {bulkStudents.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <UserIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                          <p>No students added yet. Click "Add Student" to begin.</p>
                        </div>
                      )}

                      {bulkStudents.map((student, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-4">
                          <div className="flex items-center justify-between">
                            <h6 className="font-medium text-gray-900">Student {index + 1}</h6>
                            <button
                              type="button"
                              onClick={() => removeManualStudent(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Input
                              label="First Name *"
                              value={student.firstName}
                              onChange={(e) => updateManualStudent(index, 'firstName', e.target.value)}
                              placeholder="John"
                              required
                            />
                            <Input
                              label="Last Name *"
                              value={student.lastName}
                              onChange={(e) => updateManualStudent(index, 'lastName', e.target.value)}
                              placeholder="Doe"
                              required
                            />
                            <Input
                              label="Email *"
                              type="email"
                              value={student.email}
                              onChange={(e) => updateManualStudent(index, 'email', e.target.value)}
                              placeholder="john.doe@example.com"
                              required
                            />
                            <Input
                              label="Phone"
                              value={student.phone}
                              onChange={(e) => updateManualStudent(index, 'phone', e.target.value)}
                              placeholder="+254712345678"
                            />
                            <Input
                              label="Student ID (Optional)"
                              value={student.studentId}
                              onChange={(e) => updateManualStudent(index, 'studentId', e.target.value)}
                              placeholder="Leave empty for auto-generation"
                            />
                            <Input
                              label="Grade/Class *"
                              value={student.grade}
                              onChange={(e) => updateManualStudent(index, 'grade', e.target.value)}
                              placeholder="Grade 1A"
                              required
                            />
                            <Input
                              label="Date of Birth"
                              type="date"
                              max={new Date().toISOString().split('T')[0]}
                              value={student.dateOfBirth}
                              onChange={(e) => updateManualStudent(index, 'dateOfBirth', e.target.value)}
                            />
                            <Input
                              label="Parent Email"
                              type="email"
                              value={student.parentEmail}
                              onChange={(e) => updateManualStudent(index, 'parentEmail', e.target.value)}
                              placeholder="parent@example.com"
                            />
                            <Input
                              label="Parent Phone"
                              value={student.parentPhone}
                              onChange={(e) => updateManualStudent(index, 'parentPhone', e.target.value)}
                              placeholder="+254712345678"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Preview for CSV */}
                  {bulkUploadMode === 'csv' && bulkStudents.length > 0 && (
                    <div className="space-y-4">
                      <h5 className="text-md font-medium text-gray-900">
                        Preview ({bulkStudents.length} students)
                      </h5>
                      <div className="max-h-80 overflow-auto border border-gray-200 rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Name</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Email</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Phone</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Student ID</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Grade</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Stream</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">DOB</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Parent Email</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Parent Phone</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {bulkStudents.map((student, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-sm whitespace-nowrap">{student.firstName} {student.lastName}</td>
                                <td className="px-3 py-2 text-sm whitespace-nowrap">{student.email}</td>
                                <td className="px-3 py-2 text-sm whitespace-nowrap">{student.phone || <span className="text-gray-400">-</span>}</td>
                                <td className="px-3 py-2 text-sm whitespace-nowrap">{student.studentId || <span className="text-gray-400">Auto</span>}</td>
                                <td className="px-3 py-2 text-sm whitespace-nowrap">{student.grade}</td>
                                <td className="px-3 py-2 text-sm whitespace-nowrap">{student.stream || <span className="text-gray-400">-</span>}</td>
                                <td className="px-3 py-2 text-sm whitespace-nowrap">{student.dateOfBirth || <span className="text-gray-400">-</span>}</td>
                                <td className="px-3 py-2 text-sm whitespace-nowrap">
                                  {student.parentEmail ? (
                                    <span className="text-green-600">{student.parentEmail}</span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-sm whitespace-nowrap">
                                  {student.parentPhone ? (
                                    <span className="text-green-600">{student.parentPhone}</span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-xs text-gray-500">
                        <span className="text-green-600">Green</span> = Parent will be linked if found in system
                      </p>
                    </div>
                  )}

                  {/* Progress Tracking */}
                  {bulkLoading && (
                    <div className="space-y-4 py-4 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <h5 className="text-md font-medium text-gray-900">
                          Processing Students ({bulkProgress.current} of {bulkProgress.total})
                        </h5>
                        <span className="text-sm text-gray-600">
                          {Math.round((bulkProgress.current / bulkProgress.total) * 100)}%
                        </span>
                      </div>
                      
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                        />
                      </div>

                      {/* Real-time results */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center space-x-2">
                          <CheckCircleIcon className="h-4 w-4 text-green-600" />
                          <span className="text-green-600">Success: {bulkResults.success.length}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <XCircleIcon className="h-4 w-4 text-red-600" />
                          <span className="text-red-600">Errors: {bulkResults.errors.length}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Results Summary */}
                  {!bulkLoading && (bulkResults.success.length > 0 || bulkResults.errors.length > 0) && (
                    <div className="space-y-4 py-4 border-t border-gray-200">
                      <h5 className="text-md font-medium text-gray-900">Results Summary</h5>
                      
                      {bulkResults.success.length > 0 && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h6 className="text-sm font-medium text-green-900 mb-2">
                            ✅ Successfully Enrolled ({bulkResults.success.length})
                          </h6>
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {bulkResults.success.map((student, index) => (
                              <div key={index} className="text-xs text-green-700">
                                {student.name} - {student.email}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {bulkResults.errors.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <h6 className="text-sm font-medium text-red-900 mb-2">
                            ❌ Failed to Enroll ({bulkResults.errors.length})
                          </h6>
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {bulkResults.errors.map((student, index) => (
                              <div key={index} className="text-xs text-red-700">
                                <div className="font-medium">{student.name} - {student.email}</div>
                                <div className="text-red-600 ml-2">→ {student.error}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-between pt-6 border-t border-gray-200">
                    <div className="flex space-x-3">
                      {(bulkResults.success.length > 0 || bulkResults.errors.length > 0) && !bulkLoading && (
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => {
                            setBulkResults({ success: [], errors: [] })
                            setBulkProgress({ current: 0, total: 0 })
                          }}
                        >
                          Clear Results
                        </Button>
                      )}
                    </div>
                    
                    <div className="flex space-x-3">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setShowBulkModal(false)}
                        disabled={bulkLoading}
                      >
                        {bulkLoading ? 'Processing...' : 'Cancel'}
                      </Button>
                      <Button
                        type="submit"
                        className="bg-blue-600 hover:bg-blue-700"
                        disabled={bulkLoading || bulkStudents.length === 0}
                      >
                        {bulkLoading ? (
                          <>
                            <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                            Enrolling {bulkProgress.current}/{bulkProgress.total}...
                          </>
                        ) : (
                          <>
                            <UserPlusIcon className="h-4 w-4 mr-2" />
                            Enroll {bulkStudents.length} Students
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Card Assignment Modal */}
      <AnimatePresence>
        {showCardAssignModal && cardAssignStudent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={() => setShowCardAssignModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <CreditCardIcon className="h-6 w-6 mr-2 text-blue-600" />
                    Assign RFID Card
                  </h3>
                  <button
                    onClick={() => setShowCardAssignModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Student Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-purple-200 rounded-full flex items-center justify-center">
                      <UserIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {cardAssignStudent.firstName} {cardAssignStudent.lastName}
                      </p>
                      <p className="text-sm text-gray-500">
                        {cardAssignStudent.studentId || 'No Student ID'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Card Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Available Card
                  </label>
                  {availableCards.length === 0 ? (
                    <div className="text-center py-6 bg-gray-50 rounded-lg">
                      <CreditCardIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No unassigned cards available</p>
                      <p className="text-xs text-gray-400 mt-1">Please create new cards in the RFID management section</p>
                    </div>
                  ) : (
                    <select
                      value={selectedCardId}
                      onChange={(e) => setSelectedCardId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Choose a card...</option>
                      {availableCards.map((card) => (
                        <option key={card.id} value={card.id}>
                          {card.cardNumber} ({card.cardType})
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Info Note */}
                <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700">
                  <p className="flex items-start">
                    <IdentificationIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                    <span>A 4-digit PIN will be automatically generated and sent to the student's and parent's email addresses.</span>
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCardAssignModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleCardAssignSubmit}
                  disabled={!selectedCardId || cardAssignLoading}
                  loading={cardAssignLoading}
                >
                  Assign Card
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Biometric Delete Confirmation Modal */}
      <AnimatePresence>
        {showBioDeleteModal && bioDeleteStudent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={() => !bioDeleteLoading && setShowBioDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <ExclamationTriangleIcon className="h-6 w-6 mr-2 text-red-500" />
                    Delete Biometric Data
                  </h3>
                  <button
                    onClick={() => !bioDeleteLoading && setShowBioDeleteModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                    disabled={bioDeleteLoading}
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Student Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-orange-200 rounded-full flex items-center justify-center">
                      <FingerPrintIcon className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {bioDeleteStudent.firstName} {bioDeleteStudent.lastName}
                      </p>
                      <p className="text-sm text-gray-500">
                        {bioDeleteStudent.studentId || 'No Student ID'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Warning Message */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">
                    Are you sure you want to delete the biometric data for <strong>{bioDeleteStudent.firstName} {bioDeleteStudent.lastName}</strong>?
                  </p>
                  <p className="text-sm text-red-600 mt-2 font-medium">
                    This action cannot be undone. The student will need to re-enroll their fingerprints.
                  </p>
                </div>

                {/* Biometric Info */}
                {bioDeleteStudent.onboardingStatus?.biometric && (
                  <div className="text-sm text-gray-600 space-y-1">
                    <p className="flex items-center">
                      <span className="w-32 text-gray-500">Fingerprints:</span>
                      <span className="font-medium">{bioDeleteStudent.onboardingStatus.biometric.fingerPrintCount || 0} enrolled</span>
                    </p>
                    {bioDeleteStudent.onboardingStatus.biometric.enrolledAt && (
                      <p className="flex items-center">
                        <span className="w-32 text-gray-500">Enrolled:</span>
                        <span className="font-medium">
                          {new Date(bioDeleteStudent.onboardingStatus.biometric.enrolledAt).toLocaleDateString()}
                        </span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowBioDeleteModal(false)}
                  disabled={bioDeleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  onClick={handleConfirmDeleteBiometric}
                  disabled={bioDeleteLoading}
                  loading={bioDeleteLoading}
                >
                  Delete Biometric
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* PIN Reset Confirmation Modal */}
      <AnimatePresence>
        {showPinResetModal && pinResetStudent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={() => !pinResetLoading && setShowPinResetModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <KeyIcon className="h-6 w-6 mr-2 text-primary-500" />
                    Reset Card PIN
                  </h3>
                  <button
                    onClick={() => !pinResetLoading && setShowPinResetModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                    disabled={pinResetLoading}
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Student Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center">
                      <CreditCardIcon className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {pinResetStudent.firstName} {pinResetStudent.lastName}
                      </p>
                      <p className="text-sm text-gray-500">
                        {pinResetStudent.studentId || 'No Student ID'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Info Message */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    Generate a new PIN for <strong>{pinResetStudent.firstName} {pinResetStudent.lastName}'s</strong> card?
                  </p>
                  <p className="text-sm text-blue-600 mt-2">
                    The new PIN will be sent to their registered email address.
                  </p>
                </div>

                {/* Card Info */}
                {pinResetStudent.onboardingStatus?.rfid && (
                  <div className="text-sm text-gray-600 space-y-1">
                    <p className="flex items-center">
                      <span className="w-32 text-gray-500">Card Number:</span>
                      <span className="font-medium font-mono">{pinResetStudent.onboardingStatus.rfid.cardNumber}</span>
                    </p>
                    {pinResetStudent.email && (
                      <p className="flex items-center">
                        <span className="w-32 text-gray-500">Email:</span>
                        <span className="font-medium">{pinResetStudent.email}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPinResetModal(false)}
                  disabled={pinResetLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleConfirmResetPIN}
                  disabled={pinResetLoading}
                  loading={pinResetLoading}
                >
                  Reset PIN
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default StudentsPage
